import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
API.interceptors.request.use(cfg => {
  try { const u = JSON.parse(localStorage.getItem('npd_user')); if (u?.token) cfg.headers.Authorization = 'Bearer ' + u.token; } catch {}
  return cfg;
});

const ATTRS = ['sweet','salty','sour','bitter','umami','heat','aroma_intensity'];
const NUTRI_ROWS = [
  ['energy_kcal','Energy','kcal'],['energy_kj','','kJ'],['fat','Total Fat','g'],['sat_fat','Saturated Fat','g'],
  ['trans_fat','Trans Fat','g'],['carbs','Total Carbohydrate','g'],['sugars','Total Sugars','g'],
  ['added_sugars','Added Sugars','g'],['fiber','Dietary Fiber','g'],['protein','Protein','g'],
  ['sodium_mg','Sodium','mg'],['salt_g','Salt','g']
];

export default function ProductPdfDialog({ open, product, recipe, sfgLinks, onClose }) {
  const [opts, setOpts] = useState({ details: true, recipe: true, costs: false, nutrition: true, flavor: false });
  const [busy, setBusy] = useState(false);

  if (!open) return null;
  const toggle = (k) => setOpts(o => ({ ...o, [k]: !o[k] }));

  const generate = async () => {
    setBusy(true);
    try {
      let nutri = null, flavor = null;
      if (opts.nutrition) {
        try { nutri = (await API.get('/nutrition/resolve/' + encodeURIComponent(product.product_code))).data; } catch {}
      }
      if (opts.flavor) {
        try { flavor = (await API.get('/flavor/resolve/' + encodeURIComponent(product.product_code))).data; } catch {}
      }

      // Build the hidden print layout
      const host = document.createElement('div');
      host.style.cssText = 'position:fixed;left:-10000px;top:0;width:794px;background:#fff;color:#111;font-family:Georgia,serif;padding:34px;box-sizing:border-box;';
      let html = '';

      html += `<div style="border-bottom:3px solid #7B2D6B;padding-bottom:10px;margin-bottom:16px;">
        <div style="font-size:20px;font-weight:800;">${product.name || ''}</div>
        <div style="font-size:12px;font-family:monospace;color:#7B2D6B;">${product.product_code || ''}${product.item_code ? ' · ' + product.item_code : ''}</div>
      </div>`;

      if (opts.details) {
        html += `<table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:16px;">
          ${[['Type', product.is_sfg ? 'SFG (Semi-Finished)' : 'FG (Finished Good)'],
             ['Status', product.status || '—'],
             ['Department', product.department || '—'],
             ['Product Type', product.product_type || '—'],
             ['Batch Size', recipe?.batch_size ? recipe.batch_size + ' ' + (recipe.batch_unit || 'kg') : '—']]
            .map(([k, v]) => `<tr><td style="border:1px solid #999;padding:5px 10px;font-weight:700;background:#f6f0f5;width:160px;">${k}</td><td style="border:1px solid #999;padding:5px 10px;">${v}</td></tr>`).join('')}
        </table>`;
      }

      if (opts.recipe && (recipe?.ingredients?.length || sfgLinks?.length)) {
        html += `<div style="font-weight:800;font-size:14px;margin:14px 0 6px;">Recipe / Ingredients</div>`;
        if (sfgLinks && sfgLinks.length > 0) {
          html += `<table style="width:100%;border-collapse:collapse;font-size:11px;">
            <tr style="background:#f0e6ee;"><th style="border:1px solid #999;padding:5px 8px;text-align:left;">SFG</th><th style="border:1px solid #999;padding:5px 8px;text-align:right;width:80px;">% in FG</th></tr>
            ${sfgLinks.map(s => `<tr><td style="border:1px solid #999;padding:5px 8px;">${s.sfg_name || ''}</td><td style="border:1px solid #999;padding:5px 8px;text-align:right;">${s.percentage || 0}%</td></tr>`).join('')}
          </table>`;
        } else {
          const cols = opts.costs
            ? `<th style="border:1px solid #999;padding:5px 8px;text-align:right;width:90px;">Unit Cost</th><th style="border:1px solid #999;padding:5px 8px;text-align:right;width:90px;">Total</th>` : '';
          html += `<table style="width:100%;border-collapse:collapse;font-size:11px;">
            <tr style="background:#f0e6ee;"><th style="border:1px solid #999;padding:5px 8px;text-align:left;">Ingredient</th><th style="border:1px solid #999;padding:5px 8px;text-align:right;width:90px;">Qty</th>${cols}</tr>
            ${(recipe.ingredients || []).map(i => `<tr>
              <td style="border:1px solid #999;padding:5px 8px;">${i.item_name || i.item_code}</td>
              <td style="border:1px solid #999;padding:5px 8px;text-align:right;">${i.quantity || 0} ${i.unit || 'kg'}</td>
              ${opts.costs ? `<td style="border:1px solid #999;padding:5px 8px;text-align:right;">${(i.unit_cost||0).toFixed(2)}</td><td style="border:1px solid #999;padding:5px 8px;text-align:right;">${(i.total_cost||0).toFixed(2)}</td>` : ''}
            </tr>`).join('')}
            ${opts.costs ? `<tr style="font-weight:800;"><td style="border:1px solid #999;padding:5px 8px;">Total</td><td style="border:1px solid #999;"></td><td style="border:1px solid #999;"></td><td style="border:1px solid #999;padding:5px 8px;text-align:right;">AED ${(recipe.total_cost||0).toFixed(2)}</td></tr>` : ''}
          </table>`;
        }
      }

      if (opts.nutrition && nutri?.profile) {
        const p = nutri.profile;
        const DV = { energy_kcal: 2000, fat: 78, sat_fat: 20, carbs: 275, added_sugars: 50, fiber: 28, protein: 50, sodium_mg: 2300, salt_g: 5.75 };
        const LROWS = [
          ['energy_kcal','Energy','kcal',1,0],['energy_kj','','kJ',0,1],['fat','Total Fat','g',1,0],
          ['sat_fat','Saturated Fat','g',0,1],['trans_fat','Trans Fat','g',0,1],['carbs','Total Carbohydrate','g',1,0],
          ['sugars','Total Sugars','g',0,1],['added_sugars','Added Sugars','g',0,1],['fiber','Dietary Fiber','g',0,1],
          ['protein','Protein','g',1,0],['sodium_mg','Sodium','mg',1,0],['salt_g','Salt','g',0,1]
        ];
        const factRows = LROWS.map(([k, label, unit, bold, indent]) => {
          const val = p[k]; if (val === undefined || val === null) return '';
          const dv = DV[k] ? Math.round((val / DV[k]) * 100) + '%' : '';
          return '<div style="display:flex;border-top:1px solid #999;padding:3px 0;font-size:12px;">'
            + '<div style="flex:1;' + (indent ? 'padding-left:14px;' : '') + 'font-weight:' + (bold ? 800 : 400) + ';">' + label + '</div>'
            + '<div style="padding:0 6px;">' + val + unit + '</div>'
            + '<div style="width:44px;text-align:right;font-weight:700;">' + dv + '</div></div>';
        }).join('');
        html += '<div style="width:320px;border:2px solid #111;padding:12px;font-family:Helvetica,Arial,sans-serif;margin:16px 0 4px;">'
          + '<div style="font-size:24px;font-weight:900;border-bottom:8px solid #111;padding-bottom:4px;">Nutrition Facts</div>'
          + '<div style="font-size:12px;padding:4px 0;border-bottom:4px solid #111;">Per 100 g (estimated)</div>'
          + '<div style="text-align:right;font-size:10px;padding-top:3px;">% DV*</div>'
          + factRows
          + '<div style="font-size:9px;border-top:4px solid #111;padding-top:4px;margin-top:4px;">* Percent Daily Values based on a 2,000 kcal diet.</div></div>'
          + '<div style="font-size:9px;color:#777;margin-bottom:8px;">' + (nutri.disclaimer || '') + '</div>';
      }

      if (opts.flavor && flavor?.profile) {
        const fp = flavor.profile;
        const LB = { sweet:'Sweet', salty:'Salty', sour:'Sour', bitter:'Bitter', umami:'Umami', heat:'Heat', aroma_intensity:'Aroma' };
        const cx = 130, cy = 130, R = 100, mx = 10;
        const pt = (i, r) => { const ang = (Math.PI * 2 * i) / ATTRS.length - Math.PI / 2; return [cx + r * Math.cos(ang), cy + r * Math.sin(ang)]; };
        const rings = [0.25, 0.5, 0.75, 1].map(f =>
          '<polygon points="' + ATTRS.map((a, i) => pt(i, R * f).join(',')).join(' ') + '" fill="none" stroke="#ddd" stroke-width="1"/>').join('');
        const axes = ATTRS.map((a, i) => { const xy = pt(i, R); return '<line x1="' + cx + '" y1="' + cy + '" x2="' + xy[0] + '" y2="' + xy[1] + '" stroke="#ddd" stroke-width="1"/>'; }).join('');
        const poly = '<polygon points="' + ATTRS.map((a, i) => pt(i, ((fp[a] || 0) / mx) * R).join(',')).join(' ') + '" fill="rgba(123,45,107,0.25)" stroke="#7B2D6B" stroke-width="2"/>';
        const labels = ATTRS.map((a, i) => { const xy = pt(i, R + 18); return '<text x="' + xy[0] + '" y="' + xy[1] + '" text-anchor="middle" dominant-baseline="middle" style="font-size:10px;fill:#333;">' + LB[a] + ' ' + Number(fp[a] || 0).toFixed(1) + '</text>'; }).join('');
        html += '<div style="font-weight:800;font-size:14px;margin:16px 0 6px;">Flavor Profile (resolved)</div>'
          + '<svg width="260" height="260" viewBox="0 0 260 260" xmlns="http://www.w3.org/2000/svg">' + rings + axes + poly + labels + '</svg>';
      }

      html += `<div style="font-size:9px;color:#999;margin-top:22px;border-top:1px solid #ddd;padding-top:6px;">Generated ${new Date().toLocaleDateString('en-AE')} · BGH NPD Tracker</div>`;

      host.innerHTML = html;
      document.body.appendChild(host);
      const canvas = await html2canvas(host, { scale: 2, backgroundColor: '#ffffff' });
      document.body.removeChild(host);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = 210, pageH = 297;
      const imgH = canvas.height * pageW / canvas.width;
      let heightLeft = imgH, position = 0;
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      pdf.addImage(imgData, 'JPEG', 0, position, pageW, imgH);
      heightLeft -= pageH;
      while (heightLeft > 0) {
        position -= pageH;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, pageW, imgH);
        heightLeft -= pageH;
      }
      pdf.save(((product.item_code || product.product_code || 'product') + '_details.pdf').replace(/[^A-Za-z0-9._-]/g, '-'));
      toast.success('PDF downloaded');
      onClose();
    } catch (e) { toast.error('PDF generation failed'); }
    setBusy(false);
  };

  const OPTS = [
    ['details', 'Product Details', true],
    ['recipe', 'Recipe / Ingredients', false],
    ['costs', 'Include Costs', false],
    ['nutrition', 'Nutrition Information', false],
    ['flavor', 'Flavor Profile', false],
  ];

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,8,18,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 360, background: 'var(--bg-card, #fff)', borderRadius: 14, padding: 20, boxShadow: '0 16px 48px rgba(0,0,0,0.35)' }}>
        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 12 }}>⬇ Download Product PDF</div>
        {OPTS.map(([k, label, locked]) => (
          <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 4px', fontSize: 13, cursor: locked ? 'default' : 'pointer', opacity: locked ? 0.7 : 1 }}>
            <input type="checkbox" checked={opts[k]} disabled={locked} onChange={() => toggle(k)} />
            {label}
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" disabled={busy} onClick={generate}>{busy ? 'Generating…' : 'Generate PDF'}</button>
        </div>
      </div>
    </div>
  );
}
